/*!

=========================================================
* Paper Dashboard React - v1.3.2
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

* Licensed under MIT (https://github.com/creativetimofficial/paper-dashboard-react/blob/main/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import {useRef, useParams} from "react";
// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Button,
} from "reactstrap";
import BeatLoader from "react-spinners/BeatLoader";
import NotificationAlert from "react-notification-alert";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import heatmap from 'highcharts/modules/heatmap.js';
import treemap from 'highcharts/modules/treemap.js';
heatmap(Highcharts);
treemap(Highcharts);



function ProjectHighRisk(props) {
  const ref = useRef(null);

  const [projectName, setProjectName] = React.useState((new URLSearchParams(window.location.search)).get("name"));
  const [heatmapData, setHeatmapData] = React.useState({});
  const [allHeatMapData, setAllHeatMapData] = React.useState([]);
  const [onlyHighTDHeatMapData, setOnlyHighTDHeatMapData] = React.useState([]);
  const [fileImgRootCauses, setFileImgRootCauses] = React.useState();
  const [endpointForThresholds,setEndpointForThresholds] = React.useState("");
  const [selectedFileFinished, setSelectedFileFinished] = React.useState(false);
  const [endpointForFilePlot, setEndpointForFilePlot] = React.useState("");
  const [selectedFileFromHeatmap, setSelectedFileFromHeatmap] = React.useState("");
  const [fileImgInterpretation, setFileImgInterpretation] =React.useState();


  const notificationAlert = React.useRef();
  const notify = (message) => {
    var options = {
      place: "tr",
      message: (
        <div>
          <div>
            {message}
          </div>
        </div>
      ),
      type: "warning",
      icon: "nc-icon nc-bell-55",
      autoDismiss: 7,
    };
    notificationAlert.current.notificationAlert(options);
  }

  async function handleClickStatus (){
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + '/running/hightd?owner='+process.env.REACT_APP_ORG+'&name='+projectName,{
          method: "GET",
          redirect: "follow",
          headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
        });
      const result = await response.json();
      if(response.status === 200) {
        var obj = JSON.parse(JSON.stringify(result));
        var now= ((obj.stepNow / obj.stepsTotal)*100).toFixed();
        var total= ((obj.stepsTotal / obj.stepsTotal)*100).toFixed();
        notify("High TD analysis is running for this project, please wait " +now+ "%");
      }
      else if (response.status === 404) {
        window.location.reload(false);
      }
      else {
        console.log("API error:", result);
        notify("High TD analysis is running for this project, please wait...");
      }
    } catch (error) {
      console.error("Error:", error);
      notify("High TD analysis is running for this project, please wait...");
    }
  }

  /**
   * Get projects data from API
   */
  const fetchProjectHeatmapData = async (newcommit) => {
    console.log("Fetching all projects data...");
    var url = '/highrisk/project/exist';
    if(newcommit){
      url= '/highrisk/project';
    }

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + url + '?owner='+process.env.REACT_APP_ORG+'&name=' + projectName,
        {
          method: "GET",
          redirect: "follow",
          headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
        }
      );
      const result = await response.json();
      if(response.status === 200) {
        var obj = JSON.parse(JSON.stringify(result));
        var results = obj;
        setAllHeatMapData(results);
        
        fetchProjectOnlyHighTDHeatmapData();

        //get Metrics Thresholds
        setEndpointForThresholds(process.env.REACT_APP_API_URL+"/highrisk/project/thresholds?owner="+process.env.REACT_APP_ORG+'&name='+projectName);

        //get Root Causes
        console.log("fetch img for root causes...");
        try {
          const response = await fetch(
            process.env.REACT_APP_API_URL + '/highrisk/project/causes?owner='+process.env.REACT_APP_ORG+'&name=' + projectName,
            {
              method: "GET",
              redirect: "follow",
              headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
            }
          );
          if(response.status === 200) {
            const imageBlob = await response.blob();
            const imageObjectURL = URL.createObjectURL(imageBlob);
            setFileImgRootCauses(imageObjectURL);
          }
          else {
            console.log("API error");
          }
        } catch (error) {
          console.error("Error:", error);
        }
      }
      else {
        console.log("API error:", result);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  /**
   * Get projects only high TD data from API
   */
  const fetchProjectOnlyHighTDHeatmapData = async () => {
    console.log("Fetching high TD data...");
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + '/highrisk/project/hightd?owner='+process.env.REACT_APP_ORG+'&name=' + projectName,
        {
          method: "GET",
          redirect: "follow",
          headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
        }
      );
      const result = await response.json();
      if(response.status === 200) {
        var obj = JSON.parse(JSON.stringify(result));
        var results = obj;
        setOnlyHighTDHeatMapData(results);

        setHeatmapData({
          colorAxis: {
              minColor: '#FFFFFF',
              maxColor: 'rgba(255,0,0,1)'
          },
          series: [{
              animation: false,
              type: 'treemap',
              layoutAlgorithm: 'squarified',
              turboThreshold: 3000,
              data: returnValues(results),
              tooltip: {
                  pointFormat: '<b>Risk:</b> {point.colorValue} <b>%</b>'
              },
              point: {
                events: {
                    click: function () {
                      onClickFile(this.name,this.path);
                    }
                }
              }
          }],
          title: {
              text: 'Heatmap of High-TD Probability per Class'
          }
        });
      }
      else {
        console.log("API error:", result);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  //todo
  // check...
  // file name in file, and first check existance
  async function onClickFile(name,path){
    //waiting animation
    setSelectedFileFromHeatmap(path+'');
    setSelectedFileFinished(false);

    //get plot
    console.log("fetch file plot...");
    console.log('Name: ' + name + ', paht: ' + path);
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + '/highrisk/file/plot/generate?owner='+process.env.REACT_APP_ORG+'&name='+projectName+'&file='+path,
        {
          method: "GET",
          redirect: "follow",
          headers: {Authorization: `Bearer ${localStorage.getItem("accessToken")}`}
        }
      );
      if(response.status === 200) {
        setEndpointForFilePlot(process.env.REACT_APP_API_URL+"/highrisk/file/plot?owner="+process.env.REACT_APP_ORG+'&name='+projectName+'&file='+path)
        setSelectedFileFinished(true);
      }
      else {
        console.log("API error");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }


  // Function to extract values from json
  function returnValues(data) {
    var values = []
    for(var i = 0; i < data.length; i++) {
        values.push({
            'name': data[i]['class_name'],
            'path': data[i]['class_path'],
            'value': data[i]['loc'],
            'colorValue': data[i]['high_td_proba'] * 100
        })
    }
    return values
  }



  /**
   * Fetch data from API when component is mounted
   */
  React.useEffect(() => {
    console.log("...ProjectMore component mounted...");
    var name=(new URLSearchParams(window.location.search)).get("name");
    setProjectName(name);

    fetchProjectHeatmapData(false);
  }, []);



  /**
   * Change heatmap data
   */
  function changeShowOnlyHighTD(e) {
    const element = ref.current;
    console.log(element.checked);
    if(element.checked) {
      setHeatmapData({
        colorAxis: {
            minColor: '#FFFFFF',
            maxColor: 'rgba(255,0,0,1)'
        },
        series: [{
            animation: false,
            type: 'treemap',
            layoutAlgorithm: 'squarified',
            turboThreshold: 3500,
            data: returnValues(allHeatMapData),
            tooltip: {
                pointFormat: '<b>Risk:</b> {point.colorValue} <b>%</b>'
            },
            point: {
              events: {
                  click: function () {
                    onClickFile(this.name,this.path);
                  }
              }
            }
        }],
        title: {
            text: 'Heatmap of High-TD Probability per Class'
        }
      });
    }
    else {
      setHeatmapData({
        colorAxis: {
            minColor: '#FFFFFF',
            maxColor: 'rgba(255,0,0,1)'
        },
        series: [{
            animation: false,
            type: 'treemap',
            layoutAlgorithm: 'squarified',
            turboThreshold: 3000,
            data: returnValues(onlyHighTDHeatMapData),
            tooltip: {
                pointFormat: '<b>Risk:</b> {point.colorValue} <b>%</b>'
            },
            point: {
              events: {
                  click: function () {
                    onClickFile(this.name,this.path);
                  }
              }
            }
        }],
        title: {
            text: 'Heatmap of High-TD Probability per Class'
        }
      });
    }
  }

  return (
    <>
      <div className="content">
        <NotificationAlert ref={notificationAlert} />
        <CardTitle tag="h5">{projectName}</CardTitle>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <div style={{display:"flex", justifyContent:"space-between"}}>
                  <CardTitle tag="h5" style={{display:"flex", alignContent:"center"}}>
                    Classes at Risk of Producing HIGH TD Interest
                    <i style={{marginLeft:"10px", marginTop:"auto", marginBottom:"auto", cursor: "pointer"}} onClick={()=>fetchProjectHeatmapData(true)} className="nc-icon nc-refresh-69" title="Calculate on last commit" />
                  </CardTitle>
                  {allHeatMapData.length!=0 ? 
                    <div style={{display:"flex", marginBottom:"auto", marginTop:"auto"}}>
                      <label class="switch" style={{margin:"auto"}} onChange={changeShowOnlyHighTD}>
                        <input ref={ref} id="switchTD" type="checkbox"/>
                        <span class="slider round"></span>
                      </label>
                      <span style={{margin:"auto", marginLeft:"5px"}}>Show all files</span>
                    </div>
                    :
                    <></>}
                </div>
              </CardHeader>
              <CardBody>
                {allHeatMapData.length!=0 ? 
                  <div>
                  <Col md="12">
                    <Row>
                      <Col md="12">
                        <HighchartsReact highcharts={Highcharts} options={heatmapData} immutable = {true} />
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <Card>
                          <CardHeader >
                            <CardTitle tag="h5">Interpretation of File</CardTitle>
                          </CardHeader>
                          <CardBody>
                            {selectedFileFromHeatmap == "" ? 
                              <span>Select a file..</span> :
                                <>
                                {selectedFileFinished == true ?
                                <div>
                                  <div style={{padding:"15px", overflowWrap:"anywhere"}}>
                                    <p>Fille: {selectedFileFromHeatmap}</p>
                                  </div>
                                  <div>
                                    <iframe
                                      src= {endpointForFilePlot}
                                      title="External HTML"
                                      width="100%"
                                      height="180px"
                                      frameBorder="0"
                                      style={{pointerEvents:"none"}}
                                    />
                                  </div>
                                </div>
                                :
                                <div style={{display:"flex", justifyContent:"center"}}>
                                  <BeatLoader color={"#119f50"} />
                                </div>
                                }
                                </>
                            }
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                  </div>
                  : <div style={{display:"flex", justifyContent:"center", flexDirection: "column", alignItems: "center"}}>
                      <BeatLoader color={"#119f50"} />
                      <Button className="btn-round" color="primary" onClick={handleClickStatus}>Status</Button>
                    </div>}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {allHeatMapData.length!=0 ? 
        <Row>
          <Col md="12">
            <Card>
              <CardHeader >
                <CardTitle tag="h5">HIGH TD Interest Risk: Root Cause Analysis</CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col xl="5" lg="6" md="12" sm="12">
                    <Card>
                      <CardHeader >
                        <CardTitle tag="h5">Root Causes</CardTitle>
                      </CardHeader>
                      <CardBody>
                        <img src={fileImgRootCauses} alt="root causes"></img>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl="7" lg="6" md="12" sm="12">
                    <Card>
                      <CardHeader >
                        <CardTitle tag="h5">Metrics Threshold</CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div>
                          <iframe
                            src= {endpointForThresholds}
                            title="External HTML"
                            width="100%"
                            height="400px"
                            frameBorder="0"
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        :
        <></>}
      </div>
    </>
  );
}

export default ProjectHighRisk;
